import { action, computed, makeObservable, observable } from "mobx";
import type { Agv, Mission, WarehouseLayout } from "../../agv/types";
import { initialAgvs, initialMissions, warehouseLayout } from "../../agv/data";
import { LocalStorageService } from "../../utils/local-storage";

export type ViewMode = "3d" | "2d";

export type Mode = "vehicles" | "missions" | "logs";

interface ServiceAgvState {
    enabled: boolean;
}

export class ServiceAgv extends LocalStorageService<ServiceAgvState> {
    @observable public accessor viewMode: ViewMode = "3d";
    @observable public accessor mode: Mode = "vehicles";
    @observable public accessor agvs: Agv[] = initialAgvs;
    @observable public accessor selectedAgvId: string | null = null;
    @observable public accessor selectedMissionId: string | null = null;
    @observable public accessor simulationRunning: boolean = true;

    public readonly warehouse: WarehouseLayout = warehouseLayout;
    public readonly missions: Mission[] = initialMissions;

    constructor() {
        super({
            version: 1,
            defaultValue: { enabled: false },
            identifier: "agv",
        });
        makeObservable(this);
    }

    @computed public get enabled(): boolean {
        return this.storedValue.enabled;
    }

    @action.bound public setEnabled(enabled: boolean): void {
        this.storedValue = { ...this.storedValue, enabled };
    }

    @action.bound public toggleViewMode(): void {
        this.viewMode = this.viewMode === "3d" ? "2d" : "3d";
    }

    @action.bound public setMode(mode: Mode): void {
        this.mode = mode;
    }

    @action.bound public updateAgv(id: string, update: Partial<Agv>): void {
        this.agvs = this.agvs.map((a) => (a.id === id ? { ...a, ...update } : a));
    }

    @action.bound public setSelectedAgv(id: string): void {
        this.selectedAgvId = this.selectedAgvId === id ? null : id;
    }

    @action.bound public setSelectedMissionId(id: string): void {
        this.selectedMissionId = this.selectedMissionId === id ? null : id;
    }

    @action.bound public toggleSimulation(): void {
        this.simulationRunning = !this.simulationRunning;
    }
}
